<template>
  <div class="pre-start page">

    <b-modal :active="uploadingRepair != null" :can-cancel="false">
      <div class="box" v-if="uploadingRepair">
        <b>Uploading Photo</b> {{ uploadingRepair.description }} <i class="fas fa-cog fa-spin"></i>
        <progress class="progress is-info" :value="uploadingRepair.photoUploadProgress" max="100"></progress>
      </div>
    </b-modal>

    <!-- SELECT A MACHINE -->
    <template v-if="!machine">
      <h1 class="title is-3">Select a machine to begin pre-start check list</h1>
      <machines-list v-if="!machine"
        @selected="selected"
        :show-table-head="false"
        :show-smu="false"
        :show-next-service="false"
        :show-add="false"
        :show-edit="false" />
    </template>

    <!-- COMPLETE CHECKLIST -->
    <template v-else-if="machine && !repairs">
      <div class="flex">
        <h1 class="flex-1 title is-3">{{ machine.name }}</h1>
        <button class="button is-small" @click="cancel">Cancel</button>
      </div>
      <template v-if="checkList === null">
        <loading />
      </template>

      <template v-else-if="submitting">
        <loading text="Submitting Pre Start Check List" />
      </template>
      <template v-else>
        <check-list :check-list="checkList" @submit="submit" />
      </template>
    </template>

    <!-- ADD REPAIRS -->
    <template v-else-if="machine && repairs && !complete">
      <div class="box" style="padding: 10px;">
        <h1 class="title is-4">Add Repairs</h1>

        Add repairs to <b>{{ machine.name }}</b>
        <div class="repair added" v-for="r in repairs" :key="r.count">
          <b-input v-model="r.description" placeholder="repair description" expanded />
          <div class="buttons flex" style="padding-top: 5px;">
            <div class="flex-1">
              <button class="button is-small" :class="{'is-danger': r.urgent && r.add}" @click="toggleUrgentRepair(r)">
                <fa icon="exclamation-circle" />&nbsp;urgent
              </button>
              <template v-if="!r.photoDataUrl">
                <label>
                  <div class="button is-small">
                    <fa v-if="r.preparingPhoto" icon="cog" spin />
                    <fa v-else icon="camera" />
                    &nbsp;photo
                  </div>
                  <input type="file" accept="image/*" @change="onAddRepairPhoto($event, r)" style="position: fixed; top: -99999em" />
                </label>
              </template>
              <template v-else>
                <button class="button is-small is-success" @click="onRemoveRepairPhoto(r)"><fa icon="camera" />&nbsp;photo</button>
              </template>
            </div>
            <div>
              <button class="button is-small" @click="removeRepair(r)">
                <fa icon="times" />
              </button>
            </div>
          </div>
        </div>

        <div>
          <button class="button is-small" @click="repairs.push(getBlankRepair(true))">Add Repair</button>
        </div>

        <template v-if="checkListRepairs.length">
          <br />
          Add repairs from prestart checklist
          <template v-for="r in checkListRepairs">
            <div class="checklist-repair flex" :key="r.count" v-if="!r.added">
              <div class="flex-1">{{ r.description }}</div>
              <button class="button is-small" @click="addCheckListRepair(r)"><fa icon="plus" /></button>
            </div>
          </template>
          <br />
        </template>

        <br /><br />
        <div class="buttons">
          <button class="button is-info" :class="{'is-loading': submittingRepairs}" @click="submitRepairs">Submit Repairs</button>
          <button class="button" @click="complete = true">No Repairs</button>
        </div>
      </div>
    </template>

    <template v-if="!complete && machine">
      <div class="current-repairs">
        <a name="currentRepairs" />
        <repairs :machine="machine" :can-add="false" :can-edit="false" />
      </div>
      <div class="box pad-0 margin-top-20">
        <pre-start-history :machine="machine" :limit="10" />
      </div>
    </template>

    <template v-else-if="machine && complete">
      <div class="text-center">
        <h1 class="title is-3">{{ machine.name }}</h1>
        <h1 class="title is-3">Pre Start Check Complete</h1>
        <button class="button" @click="cancel">Continue</button>
      </div>
    </template>

    <!-- ERROR MESSAGES -->
    <b-message v-if="errMsg.length" type="is-danger">{{ errMsg }}</b-message>

  </div>
</template>

<script>
import { RepairsHelper } from '@/utils/repairs'
import MachinesList from '@/components/MachinesList'
import CheckList from '@/components/CheckList'
import Repairs from '@/components/Repairs'
import PreStartHistory from '@/components/PreStartHistory'

import firebase from 'firebase/app'
import 'firebase/storage'

export default {
  name: 'PreStart',
  components: {
    MachinesList,
    CheckList,
    Repairs,
    PreStartHistory
  },
  data () {
    return {
      machine: null,
      checkList: null,
      coord: null,
      address: null,
      submitting: false,
      errMsg: '',
      showRapairsModal: false,
      checkListRepairs: null,
      repairs: null,
      submittingRepairs: false,
      counter: 0,
      complete: false,
      uploadingRepair: null
    }
  },
  computed: {
    user () { return this.$store.getters.user }
  },
  watch: {
    machine () {
      if (!this.machine) {
        this.checkList = null
        return
      }

      this.$firestore.collection('checkLists')
        .doc(this.machine.preStartCheckListId)
        .get()
        .then(doc => {
          let checkList = this.$utils.docToObj(doc)
          this.prepareCheckList(checkList)
        })
    }
  },
  methods: {
    cancel () {
      this.machine = null
      this.checkList = null
      this.repairs = null
      this.checkListRepairs = null
      this.submitting = false
      this.submittingRepairs = false
      this.complete = false
    },
    prepareCheckList (checkList) {
      if (checkList.requiresSmu) {
        checkList.checks.unshift({
          name: 'Enter SMU ' + this.$options.filters.prettyUnits(this.machine.units),
          type: 'number',
          mandatory: true
        })
      }
      this.getLocation()
      checkList.machine = this.machine.name
      checkList.machineId = this.machine._id
      checkList.user = `${this.user.firstName} ${this.user.lastName}`
      checkList.userId = this.user.uid
      checkList.checkLisyId = checkList._id
      delete checkList._id
      this.checkList = checkList
    },
    selected (machine) {
      if (!machine.preStartCheckListId) {
        return this.$dialog.alert({
          type: 'is-danger',
          title: 'Not Prestart Checklist',
          message: `${machine.name} does not have a prestart checklist.`
        })
      }
      this.machine = machine
    },
    getLocation () {
      this.coord = null
      if (!navigator.geolocation) {
        return
      }
      navigator.geolocation.getCurrentPosition(position => {
        this.coord = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        }
        console.log(this.coord)
        this.geoLocate()
      })
    },
    geoLocate () {
      this.address = null
      let apiKey = this.$store.getters.googleApiKey
      let url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${this.coord.lat},${this.coord.lng}&key=${apiKey}`
      fetch(url)
        .then(resp => resp.json())
        .then(data => {
          console.log('GEOLOCATE DATA', data)
          if (data.results && data.results.length) {
            this.address = data.results[0].formatted_address
          }
        })
        .catch(err => {
          console.log('GEO LOCATE ERROR', err)
        })
    },
    submit (checkList) {
      this.submitting = true
      checkList.address = this.address
      checkList.coord = this.coord
      console.log(checkList)
      this.errMsg = ''
      this.$firestore.collection('preStartChecks')
        .add(checkList)
        .then(resp => this.updateMachine(checkList))
        .catch(err => {
          this.submitting = false
          this.errMsg = 'ERROR: Could not submit pre-start checklist'
          console.log('Add Checklist ERROR', err)
        })
    },
    updateMachine (checkList) {
      let update = {
        smu: Number(checkList.checks[0].value),
        timestampSmuUpdated: this.$moment().unix()
      }
      this.$firestore.collection('machines')
        .doc(this.machine._id)
        .update(update)
        .then(resp => {
          this.$toast.open({
            message: `${this.machine.name} pre-start check list submitted!`,
            type: 'is-info'
          })
          this.submitting = false
          this.repairs = []
          this.checkListRepairs = this.getCheckListRepairs(checkList)
        })
        .catch(err => {
          this.submitting = false
          this.errMsg = 'ERROR: Checklist submitted but could not update machine SMU'
          console.log('Update Machine ERROR', err)
        })
    },
    async submitRepairs () {
      for (let repair of this.repairs) {
        repair.description = repair.description.trim()
        if (!repair.description.length) {
          this.submitRepairs()
          continue
        }

        if (repair.photoDataUrl) {
          repair.photoUrl = await this.uploadRepairPhoto(repair)
        }

        delete repair.add
        delete repair.count
        delete repair.photoDataUrl
        delete repair.photoUploadProgress

        let resp = await this.$firestore.collection('machineRepairs').add(repair)
        if (repair.urgent) {
          RepairsHelper.alertUrgentRepair(resp.id)
        }
      }

      this.complete = true
    },
    uploadRepairPhoto (repair) {
      return new Promise((resolve, reject) => {
        this.uploadingRepair = repair
        let storageRef = firebase.storage().ref()
        let uploadTask = storageRef.child(repair.photoPath).putString(repair.photoDataUrl, 'data_url')
        repair.photoUploadProgress = 0
        uploadTask.on('state_changed',
          (snapshot) => {
            repair.photoUploadProgress = ((snapshot.bytesTransferred / snapshot.totalBytes) * 100).toFixed(0)
          },
          (error) => {
            reject(error.code)
          },
          () => {
            uploadTask.snapshot.ref.getDownloadURL()
              .then(url => {
                this.uploadingRepair = null
                resolve(url)
              })
          })
      })
    },
    addCheckListRepair (r) {
      r.added = true
      this.repairs.push(r)
    },
    getCheckListRepairs (checkList) {
      let repairs = []
      for (let check of checkList.checks) {
        if (check.comment.length) {
          let r = this.getBlankRepair()
          r.description = check.comment
          repairs.push(r)
        }
        if (check.type === 'text' && check.value.length) {
          let r = this.getBlankRepair()
          r.description = check.value
          repairs.push(r)
        }
      }
      return repairs
    },
    toggleUrgentRepair (r) {
      r.urgent = !r.urgent
      if (r.urgent) {
        r.add = true
      }
    },
    removeRepair (repair) {
      let i = this.repairs.findIndex(r => r.count === repair.count)
      if (i >= 0) {
        this.repairs.splice(i, 1)
      }
      repair.added = false
    },
    onAddRepairPhoto (e, repair) {
      if (!e.target || !e.target.files || !e.target.files.length) {
        return
      }
      repair.preparingPhoto = true
      setTimeout(() => {
        let file = e.target.files[0]

        let fileReader = new FileReader()
        fileReader.onload = (onloadEvent) => {
          repair.photoName = file.name
          repair.photoDataUrl = onloadEvent.target.result
          repair.uploadProgress = -1
          repair.photoPath = `repair-images/machine-${this.machine._id}-${Number(new Date())}`
          repair.preparingPhoto = false
        }
        fileReader.readAsDataURL(file)
      }, 300)
    },
    onRemoveRepairPhoto (repair) {
      repair.photoDataUrl = null
    },
    getBlankRepair (add = false) {
      let repair = RepairsHelper.getBlankRepair()
      repair.photoDataUrl = null
      repair.photoPath = null
      repair.photoUploadProgress = 0
      repair.preparingPhoto = false
      repair.machine = this.machine.name
      repair.machineId = this.machine._id
      repair.add = add
      repair.count = this.counter++
      return repair
    }
  }
}
</script>

<style>
  .pre-start .repair {
     margin: 5px 0;
     border-radius: 4px;
     padding: 5px;
     border: solid 2px transparent;
     background-color: hsl(0, 0%, 96%);
  }

  .pre-start .repair.added {
    border-color: hsl(0, 0%, 48%);
    background-color: hsl(0, 0%, 86%);
  }

  .pre-start .current.repair {
    background-color: #eee;
  }

  /* .pre-start .current.repair.added {

  } */

  .pre-start .repair span.check {
    margin-top: 5px;
  }

  .pre-start .current-repairs {
    margin-top: 10px;
    border: solid 1px #ccc;
    background-color: #eee;
  }

  .pre-start .checklist-repair {
    border: solid 1px #ccc;
    background-color: #eee;
    border-radius: 4px;
    margin: 3px 0;
    padding: 2px 2px 2px 10px;
  }
</style>
